import { useState } from 'react';
import { Select } from 'antd';

import { useLetterhead } from '@/api/bookings';
import { useStockSummary } from '@/api/reports';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { hueClasses } from '@/components/hues';
import { Button, Card, EmptyState, PageHeading } from '@/components/PageHeading';
import type { StockCounts } from '@core/api/reports';
import { formatAmount } from '@core/format/money';
import {
  categoryChoices,
  costShown,
  inScope,
  stockCell,
  stockColumns,
  stockFigures,
  stockFootnote,
  stockSentence,
  stockShares,
  stockSheet,
} from '@core/reports/stockSummary';
import { can } from '@core/session/capability';
import type { Session } from '@core/session/types';
import { PrintFrame } from '../print/PrintFrame';
import { reportPaperFor, reportPapers } from '../print/paper';
import { StockSummarySheet } from '../print/StockSummarySheet';
import { saveWorkbook } from '../units/import/workbook';

/** Each place's colour, on its figure and its share of the bar. Damaged is the one that needs someone. */
const placeTile: Record<string, string> = {
  ready: hueClasses.primary.tile,
  booked: hueClasses.cyan.tile,
  withCustomers: hueClasses.indigo.tile,
  workroom: hueClasses.purple.tile,
  damaged: hueClasses.warning.tile,
  moving: hueClasses.secondary.tile,
};

/** A register row: a category's subtotal, a design, or one of an opened design's sizes and colours. */
interface Row {
  id: string;
  kind: 'category' | 'design' | 'variant';
  label: string;
  code: string;
  itemId: number | null;
  counts: StockCounts;
  total: number;
  atCost: number | null;
  withoutCost: number;
}

const rupees = (value: number) => `₹${formatAmount(value)}`;

const formatCost = (line: Pick<Row, 'atCost' | 'total' | 'withoutCost'>) => {
  const cost = costShown(line);

  return cost === null ? '—' : rupees(cost);
};

/** The category picker's choice for every category — antd wants an option value that is not null. */
const allCategories = 'all';

/**
 * Reports › Stock summary — report B2, direction A of canvas "Stock summary" (owner, 2026-10-29):
 * the band says what the branch holds; figures and a bar say where the garments are; the register
 * runs by category and design, and a design opens to a line per size and colour with the same
 * columns. Every figure is the server's; the words and the columns are the core's
 * (`reports/stockSummary.ts`). The value at cost is read only by a session holding
 * `Report:ViewFinancials`, on its own path.
 */
export function StockSummaryReport({ session, onBack }: { session: Session; onBack: () => void }) {
  const branchId = session.branch?.id;
  const atCost = can(session, 'Report:ViewFinancials');
  const report = useStockSummary(branchId, atCost);
  const letterhead = useLetterhead(branchId);
  const branchName = session.branch?.name ?? '';

  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [opened, setOpened] = useState<ReadonlySet<number>>(() => new Set());
  const [printing, setPrinting] = useState(false);

  // Everything below reads the report as the chosen category sees it; the picker offers them all.
  const all = report.data;
  const summary = all ? inScope(all, categoryId) : undefined;

  if (printing && summary) {
    const columns = stockColumns(summary);

    return (
      <PrintFrame
        onClose={() => setPrinting(false)}
        title="Stock summary"
        sizes={reportPapers}
        start={reportPaperFor(columns.length + (summary.atCost === null ? 2 : 3))}
      >
        {(paper) => (
          <StockSummarySheet
            paper={paper}
            summary={summary}
            letterhead={letterhead.data}
            branchName={branchName}
          />
        )}
      </PrintFrame>
    );
  }

  const toggle = (itemId: number) =>
    setOpened((current) => {
      const next = new Set(current);

      if (!next.delete(itemId)) {
        next.add(itemId);
      }

      return next;
    });

  const exportSheet = () => {
    if (!summary) {
      return;
    }

    const sheet = stockSheet(summary);

    void saveWorkbook(
      `stock-summary-${summary.asOf}.xlsx`,
      'Stock summary',
      sheet.headers,
      sheet.rows,
    );
  };

  const rows: Row[] = summary
    ? summary.categories.flatMap((category) => [
        {
          id: `c-${category.categoryId}`,
          kind: 'category' as const,
          label: category.name || 'No category',
          code: '',
          itemId: null,
          counts: category.counts,
          total: category.total,
          atCost: category.atCost,
          withoutCost: category.designs.reduce((sum, design) => sum + design.withoutCost, 0),
        },
        ...category.designs.flatMap((design) => [
          {
            id: `d-${design.itemId}`,
            kind: 'design' as const,
            label: design.name,
            code: design.code,
            itemId: design.itemId,
            counts: design.counts,
            total: design.total,
            atCost: design.atCost,
            withoutCost: design.withoutCost,
          },
          ...(opened.has(design.itemId)
            ? design.variants.map((variant) => ({
                id: `v-${variant.variantId}`,
                kind: 'variant' as const,
                label: variant.label || 'One size',
                code: '',
                itemId: null,
                counts: variant.counts,
                total: variant.total,
                atCost: variant.atCost,
                withoutCost: variant.withoutCost,
              }))
            : []),
        ]),
      ])
    : [];

  const countClass = (row: Row) =>
    `font-mono-nums text-xs ${row.kind === 'category' ? 'font-bold text-gray-900' : row.kind === 'variant' ? 'text-default' : ''}`;

  const columns: DataViewColumn<Row>[] = [
    {
      title: 'Design',
      key: 'design',
      primary: true,
      render: (_, row) =>
        row.kind === 'category' ? (
          <span className="text-[11px] font-bold tracking-[0.06em] text-gray-900 uppercase">
            {row.label}
          </span>
        ) : row.kind === 'design' ? (
          <button
            type="button"
            aria-expanded={opened.has(row.itemId ?? 0)}
            onClick={() => toggle(row.itemId ?? 0)}
            className={`inline-flex items-center gap-1.5 text-left text-xs font-semibold ${
              opened.has(row.itemId ?? 0) ? 'text-primary' : 'hover:text-primary text-gray-900'
            }`}
          >
            <i
              className={`icon ${opened.has(row.itemId ?? 0) ? 'icon-chevron-down' : 'icon-chevron-right'}`}
              aria-hidden="true"
            />
            {row.label}
            {row.code && (
              <span className="font-mono-nums text-default font-medium">{row.code}</span>
            )}
          </button>
        ) : (
          <span className="text-default ps-5 text-xs">{row.label}</span>
        ),
    },
    ...(summary ? stockColumns(summary) : []).map((column): DataViewColumn<Row> => ({
      title: column.label,
      key: column.key,
      align: 'right',
      render: (_, row) => {
        const value = column.value(row.counts);

        // A size with nothing ready is the line a clerk is looking for.
        if (row.kind === 'variant' && column.key === 'ready' && value === 0) {
          return <span className="font-mono-nums text-warning text-xs font-bold">0</span>;
        }

        return (
          <span className={countClass(row)}>
            {row.kind === 'category' ? value : stockCell(value)}
          </span>
        );
      },
    })),
    {
      title: 'Total',
      key: 'total',
      align: 'right',
      render: (_, row) => (
        <span className="font-mono-nums text-xs font-bold text-gray-900">{row.total}</span>
      ),
    },
    ...(summary?.atCost !== null && summary !== undefined
      ? [
          {
            title: 'At cost',
            key: 'atCost',
            align: 'right' as const,
            trailing: true,
            render: (_: unknown, row: Row) => (
              <span className={countClass(row)}>
                {formatCost(row)}
                {row.withoutCost > 0 && (
                  <span
                    className="text-warning ms-1"
                    title={`${row.withoutCost} with no purchase cost`}
                  >
                    *
                  </span>
                )}
              </span>
            ),
          },
        ]
      : []),
  ];

  const footnote = summary ? stockFootnote(summary) : null;

  return (
    <>
      <PageHeading
        eyebrow="Reports · Stock"
        title="Stock summary"
        subtitle={summary ? stockSentence(summary, branchName, rupees) : 'Reading the stock…'}
        actions={
          <>
            <label className="flex flex-col gap-1 text-[11px] text-[#ffffff]">
              Category
              <Select<number | typeof allCategories>
                value={categoryId ?? allCategories}
                onChange={(value) => setCategoryId(value === allCategories ? null : value)}
                style={{ width: 200 }}
                options={[
                  { value: allCategories, label: 'All categories' },
                  ...(all ? categoryChoices(all) : []).map((choice) => ({
                    value: choice.id,
                    label: choice.label,
                  })),
                ]}
              />
            </label>
            <Button
              icon="icon icon-printer"
              shortcut="print"
              disabled={!summary}
              onClick={() => setPrinting(true)}
            >
              Print
            </Button>
            <Button icon="icon icon-file-spreadsheet" disabled={!summary} onClick={exportSheet}>
              Excel
            </Button>
            <Button icon="icon icon-arrow-left" shortcut="back" onClick={onBack}>
              Reports
            </Button>
          </>
        }
      />

      {summary && summary.total > 0 && (
        <div className="mb-4">
          <Card>
            <ul className="m-0 grid grid-cols-2 gap-4 ps-0 md:grid-cols-[repeat(auto-fit,minmax(150px,1fr))]">
              {stockFigures(summary).map((figure) => (
                <li key={figure.key} className="mb-0 flex list-none flex-col gap-0.5">
                  <span className="text-default flex items-center gap-1.5 text-[12px]">
                    <span
                      className={`size-2.5 rounded-[3px] ${placeTile[figure.key]}`}
                      aria-hidden="true"
                    />
                    {figure.label}
                  </span>
                  <span className="font-serif text-[26px] leading-tight font-semibold text-gray-900 lining-nums tabular-nums">
                    {figure.value}
                  </span>
                  {figure.detail && (
                    <span className="text-default text-[11px]">{figure.detail}</span>
                  )}
                </li>
              ))}
            </ul>

            <div
              role="img"
              aria-label={stockShares(summary)
                .map((part) => `${part.value} ${part.label.toLowerCase()}`)
                .join(', ')}
              className="mt-4 flex h-3 gap-0.5 overflow-hidden rounded-full"
            >
              {stockShares(summary).map((part) => (
                <div
                  key={part.key}
                  className={placeTile[part.key]}
                  style={{ width: `${part.share * 100}%` }}
                />
              ))}
            </div>

            {footnote && <p className="text-default mt-3 mb-0 text-[12px]">{footnote}</p>}
          </Card>
        </div>
      )}

      <Card flush>
        <div className="flex flex-wrap items-center justify-between gap-2 px-5 pt-4 pb-2">
          <p className="mb-0 text-[length:var(--scale-list-name)] font-semibold text-gray-900">
            By design
          </p>
          <span className="text-default text-[11px]">
            Open a design to see its sizes and colours
          </span>
        </div>
        <DataView
          columns={columns}
          dataSource={rows}
          loading={report.isLoading}
          empty={
            <EmptyState
              icon="icon icon-shirt"
              title={report.isError ? 'The stock could not be read' : 'No garments in stock yet'}
            />
          }
        />
      </Card>

      <p className="text-default mt-3 text-[11px]">
        As it stands now. Ready is on the rail with nothing promised; booked is on the rail and
        promised to a booking or hold. The workroom is the cleaner, the tailor, and garments back
        from a customer waiting to be checked.
        {atCost &&
          ' At cost is each garment’s own purchase cost; a * marks lines with garments that have none.'}
      </p>
    </>
  );
}
